// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type DateOnly, today, type WorkOrder, type WorkOrderPaginated, useEvolvePatch } from '@evolvemep/api-ts';
import { Text, Flex } from '@mantine/core';

import { DatePickerWrapped } from 'components/Mantine/DateInputWrapped';
import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNil } from 'helpers/isNotNil';

type Props = ModalRegistryManagedProps & {
  onUpdate: (updatedWorkOrder: WorkOrder) => void;
  onComplete: () => void;
  workOrder: WorkOrder | WorkOrderPaginated;
};

export const NeedsNeedByModal = ({ workOrder, onUpdate, onComplete, ...props }: Props) => {
  const [needByDate, setNeedByDate] = useState<DateOnly | null>(null);
  const { apiCall: updateNeedByDate, loading } = useEvolvePatch(`shop/workOrder/${workOrder.workOrderId}`);
  return (
    <WrappedModal
      title="Set need by date"
      disabled={isNil(needByDate)}
      loading={loading}
      onConfirm={async () => {
        if (isNil(needByDate)) return;
        await updateNeedByDate({ needByDate }).then(onUpdate).then(onComplete);
      }}
      confirmationText="Submit"
      size="xs"
      {...props}
    >
      <Text>A work order cannot be submitted without a need by date.</Text>
      <Flex justify="center">
        <DatePickerWrapped value={needByDate} onChange={(date) => setNeedByDate((d) => date ?? d)} minDate={today()} />
      </Flex>
    </WrappedModal>
  );
};
